/**
 * Kudu applet postMessage protocol — the single source of truth for every
 * message exchanged between an applet (inside a sandboxed cross-origin iframe)
 * and its Kudu host. Consumed by both `@kudu/applet-sdk` (applet side) and the
 * frontend-next `AppletFrame` host (via the `@kudu/applet-protocol` alias).
 *
 * Framework- and dependency-free on purpose: this file is type-checked by both
 * the applets workspace and frontend-next, so it must stay plain TypeScript.
 *
 * Every message is wrapped in an envelope tagged with a fixed channel string
 * and a protocol version so unrelated `message` events (extensions, other
 * embeds, dev tooling) are cheaply ignored, and so the version can evolve.
 */

export const APPLET_CHANNEL = 'kudu-applet' as const;
export const APPLET_PROTOCOL_VERSION = 1 as const;

/** Light/dark plus an optional bag of design tokens the host chooses to share. */
export interface AppletTheme {
  mode: 'light' | 'dark';
  /** Neutral CSS values (bg/surface/text/border/accent) the applet may adopt. */
  tokens?: Record<string, string>;
}

/** How an applet wants to be sized by the host. Declared in the `ready` message. */
export type AppletSizing =
  | { mode: 'aspect-ratio'; ratio: number }
  | { mode: 'dynamic' };

/** A fire-and-forget telemetry event. `type` is applet-defined. */
export interface AppletEvent {
  type: string;
  [key: string]: unknown;
}

interface Envelope<T extends string> {
  channel: typeof APPLET_CHANNEL;
  v: typeof APPLET_PROTOCOL_VERSION;
  type: T;
}

/* ----------------------------- Applet → Host ----------------------------- */

export interface ReadyMessage extends Envelope<'ready'> {
  appletId?: string;
  sizing?: AppletSizing;
}

export interface ResizeMessage extends Envelope<'resize'> {
  /** Preferred content height in CSS pixels. */
  height: number;
}

export interface ReportEventMessage extends Envelope<'report-event'> {
  event: AppletEvent;
}

export interface SaveStateMessage extends Envelope<'save-state'> {
  key?: string;
  state: unknown;
}

export interface LoadStateMessage extends Envelope<'load-state'> {
  requestId: string;
  key?: string;
}

export type AppletToHostMessage =
  | ReadyMessage
  | ResizeMessage
  | ReportEventMessage
  | SaveStateMessage
  | LoadStateMessage;

/* ----------------------------- Host → Applet ----------------------------- */

export interface InitMessage extends Envelope<'init'> {
  config: Record<string, unknown>;
  theme: AppletTheme;
}

export interface ThemeMessage extends Envelope<'theme'> {
  theme: AppletTheme;
}

export interface SaveStateAckMessage extends Envelope<'save-state-ack'> {
  /** v1: always false — persistence is not implemented yet (the design seam). */
  supported: boolean;
}

export interface LoadStateResultMessage extends Envelope<'load-state-result'> {
  requestId: string;
  supported: boolean;
  state: unknown;
}

/**
 * Ask the applet to reset itself to its pristine, deployed state: discard local
 * work and reload the original content. The host sends this only after the user
 * confirms; the applet decides what "reset" means for it (e.g. the JupyterLite
 * bridge clears this assignment's local storage and reloads).
 */
export type ResetMessage = Envelope<'reset'>;

export type HostToAppletMessage =
  | InitMessage
  | ThemeMessage
  | SaveStateAckMessage
  | LoadStateResultMessage
  | ResetMessage;

export type AppletMessage = AppletToHostMessage | HostToAppletMessage;

/* ------------------------------ Validation ------------------------------- */

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null;
}

function isEnvelope(value: unknown): value is Envelope<string> {
  return (
    isRecord(value) &&
    value.channel === APPLET_CHANNEL &&
    value.v === APPLET_PROTOCOL_VERSION &&
    typeof value.type === 'string'
  );
}

function isFiniteNumber(value: unknown): value is number {
  return typeof value === 'number' && Number.isFinite(value);
}

/**
 * Structurally validate an inbound value as a message the host may receive from
 * an applet. Returns the narrowed message or `null` for anything malformed so
 * callers can ignore unknown/foreign messages instead of throwing.
 */
export function parseAppletToHostMessage(
  value: unknown,
): AppletToHostMessage | null {
  if (!isEnvelope(value)) {
    return null;
  }

  switch (value.type) {
    case 'ready':
      return value as ReadyMessage;
    case 'resize':
      return isFiniteNumber((value as ResizeMessage).height)
        ? (value as ResizeMessage)
        : null;
    case 'report-event':
      return isRecord((value as ReportEventMessage).event) &&
        typeof (value as ReportEventMessage).event.type === 'string'
        ? (value as ReportEventMessage)
        : null;
    case 'save-state':
      return value as SaveStateMessage;
    case 'load-state':
      return typeof (value as LoadStateMessage).requestId === 'string'
        ? (value as LoadStateMessage)
        : null;
    default:
      return null;
  }
}

/**
 * Structurally validate an inbound value as a message an applet may receive
 * from its host. Returns the narrowed message or `null` for anything malformed.
 */
export function parseHostToAppletMessage(
  value: unknown,
): HostToAppletMessage | null {
  if (!isEnvelope(value)) {
    return null;
  }

  switch (value.type) {
    case 'init':
      return isRecord((value as InitMessage).config) &&
        isValidTheme((value as InitMessage).theme)
        ? (value as InitMessage)
        : null;
    case 'theme':
      return isValidTheme((value as ThemeMessage).theme)
        ? (value as ThemeMessage)
        : null;
    case 'save-state-ack':
      return typeof (value as SaveStateAckMessage).supported === 'boolean'
        ? (value as SaveStateAckMessage)
        : null;
    case 'load-state-result':
      return typeof (value as LoadStateResultMessage).requestId === 'string' &&
        typeof (value as LoadStateResultMessage).supported === 'boolean'
        ? (value as LoadStateResultMessage)
        : null;
    case 'reset':
      return value as ResetMessage;
    default:
      return null;
  }
}

function isValidTheme(value: unknown): value is AppletTheme {
  return isRecord(value) && (value.mode === 'light' || value.mode === 'dark');
}

/* ------------------------------ Constructors ----------------------------- */

function envelope<T extends string>(type: T): Envelope<T> {
  return { channel: APPLET_CHANNEL, v: APPLET_PROTOCOL_VERSION, type };
}

export const messages = {
  ready(appletId?: string, sizing?: AppletSizing): ReadyMessage {
    return { ...envelope('ready'), appletId, sizing };
  },
  resize(height: number): ResizeMessage {
    return { ...envelope('resize'), height };
  },
  reportEvent(event: AppletEvent): ReportEventMessage {
    return { ...envelope('report-event'), event };
  },
  saveState(state: unknown, key?: string): SaveStateMessage {
    return { ...envelope('save-state'), state, key };
  },
  loadState(requestId: string, key?: string): LoadStateMessage {
    return { ...envelope('load-state'), requestId, key };
  },
  init(config: Record<string, unknown>, theme: AppletTheme): InitMessage {
    return { ...envelope('init'), config, theme };
  },
  theme(theme: AppletTheme): ThemeMessage {
    return { ...envelope('theme'), theme };
  },
  saveStateAck(supported: boolean): SaveStateAckMessage {
    return { ...envelope('save-state-ack'), supported };
  },
  loadStateResult(
    requestId: string,
    supported: boolean,
    state: unknown,
  ): LoadStateResultMessage {
    return { ...envelope('load-state-result'), requestId, supported, state };
  },
  reset(): ResetMessage {
    return envelope('reset');
  },
};
